import { useEffect, useRef, useState } from "react";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { Helmet } from "react-helmet-async";
import { IoMdWallet } from "react-icons/io";
import {
	HiNewspaper,
	HiOutlineLockClosed,
	HiOutlineLockOpen,
} from "react-icons/hi";
import OrangeButton from "../../../components/buttons/OrangeButton";
import AccountModalForm from "./modals/AccountModalForm";
import {
	useGetOrganization,
	useGetOrganizationBySearch,
} from "../../../services/organization.service";
import { OrganizationDto, STATUS } from "../../../models/organization.dto";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../helpers/useManageTable";
import { Avatar, Popover } from "@mui/material";
import { APP_CONFIG } from "../../../helpers/appHelpers";
import ViewOrganizationInfo from "./modals/ViewOrganizationInfo";
import TableFilter from "../../../components/table/filter/TableFilter";
import { useGetCountry } from "../../../services/country.service";
import { useGetSubscriptionTier } from "../../../services/subscription-tier.service";
import { useGetBillingType } from "../../../services/billing-type.service";
import APISearchInput from "../../../components/search/APISearchInput";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import useEditFormStore from "../../../state-management/useEditFormStore";
import { BsPencil } from "react-icons/bs";
import { LuEye } from "react-icons/lu";
import CustomStyledTable from "../../../components/table/CustomStyledTable";
import AccountActionModal from "./modals/AccountActionModal";
import ManageTableColumns from "../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../helpers/useColumnVisibilityModel";
import API from "../../../services/utils/api";
import { useAsyncEffect, useLockFn } from "ahooks";
import { useSearchParams } from "react-router-dom";
import {
	accountManagementColumns,
	organizationsColumnVisibility,
} from "./tanstack-columns";
import TanStackEmployeeTable from "../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../helpers/useTableConfiguration";
import { TableId } from "../../../models/table-configuration.dto";
import useUserStore from "../../../state-management/useUserStore";

const AccountsManagement = () => {
	const [searchParams] = useSearchParams();
	const newData = searchParams.get("newData");
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [totalOrganizations, setTotalOrganizations] = useState<number>();
	const [totalCompanies, setTotalCompanies] = useState<number>();
	const [openAccountModalForm, setOpenAccountModalForm] = useState(false);
	const [openViewDetailsModal, setOpenViewDetailsModal] = useState(false);
	const [openAccountActionModal, setOpenAccountActionModal] = useState(false);
	const [accountActionMode, setAccountActionMode] = useState<
		"ACTIVATE" | "SUSPEND"
	>("ACTIVATE");
	const [isEditForm, setIsEditForm] = useState(false);
	const { formDataValues, setFormData } = useEditFormStore();
	const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(organizationsColumnVisibility);
	const currentUser = useUserStore().currentUser.user;

	useAsyncEffect(
		useLockFn(async () => {
			try {
				const { data } = await API.OrganizationAPI.getOrganizationCount();
				setTotalOrganizations(data?.count);
			} finally {
			}
		}),
		[newData],
	);

	useAsyncEffect(
		useLockFn(async () => {
			try {
				const { data } = await API.ProcessingCompanyAPI.getCompanyCount();
				setTotalCompanies(data?.count);
			} finally {
			}
		}),
		[],
	);

	const { mutate: searchOrganizations } = useGetOrganizationBySearch();
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<OrganizationDto>({
		fetchHook: useGetOrganization,
	});

	const { reloadTableData } = useManageTableWithSearchParamsData();
	const options = {
		active: true,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: "name:asc" as any,
	};
	const { data: countries } = useGetCountry(options);
	const { data: subscriptionTiers } = useGetSubscriptionTier(options);
	const { data: billingTypes } = useGetBillingType({
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: "name:asc" as any,
	});

	const handleClose = () => setAnchorEl(null);

	const additionalColumns = [
		{
			field: "name",
			headerName: "Name",
			minWidth: 280,
			flex: 1,
			headerClassName: "super-app-theme--header",
			renderCell: (params: any) => {
				return (
					<div className="flex items-center space-x-4">
						<Avatar
							src={params.row.logoUrl}
							alt="logo"
							className="w-10 h-10 rounded-lg object-cover"
						/>
						<p className="text-sm">{params.row.name}</p>
					</div>
				);
			},
		},
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	return (
		<AdminLayout>
			<Helmet>
				<title>Accounts Management | Admin Flow</title>
			</Helmet>
			<h1 className="font-bold text-xl mb-6">Accounts Management</h1>
			<div className="flex justify-between items-center space-x-6">
				<div className="flex items-center space-x-3 border w-full px-5 rounded-lg py-8">
					<div className="bg-primary-mango text-white px-2.5 py-[11px] rounded-lg">
						<IoMdWallet className="text-base" />
					</div>
					<div>
						<p className="text-[11px] text-secondary-darkgray">
							Total Number Of Accounts
						</p>
						<h3 className="font-medium text-lg">
							{totalOrganizations || "--"}
						</h3>
					</div>
				</div>
				<div className="flex items-center space-x-3 border w-full px-5 rounded-lg py-8">
					<div className="bg-primary-violet text-white px-2.5 py-[11px] rounded-lg">
						<HiNewspaper className="text-base" />
					</div>
					<div>
						<p className="text-[11px] text-secondary-darkgray">
							Total Number of companies
						</p>
						<h3 className="font-medium text-lg">{totalCompanies || "--"}</h3>
					</div>
				</div>
			</div>
			<div className="my-6 w-full flex justify-between items-center">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchOrganizations}
					/>
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{
										title: "Name",
										fieldName: "name",
									},
									{
										title: "Date Created",
										fieldName: "createdAt",
									},
								],
							},
							{
								title: "Status",
								fieldName: "status",
								fieldType: "CHECKBOX_LIST",
								options: STATUS,
							},
							{
								title: "Country",
								fieldName: "countryId",
								fieldType: "SELECT_FIELD",
								options: countries?.data || [],
							},
							{
								title: "Subscription Tier",
								fieldName: "subscriptionTierId",
								fieldType: "SELECT_FIELD",
								options: subscriptionTiers?.data || [],
							},
							{
								title: "Billing Type",
								fieldName: "billingTypeId",
								fieldType: "SELECT_FIELD",
								options: billingTypes,
							},
						]}
					/>
					<ManageTableColumns
						columns={accountManagementColumns}
						defaultVisibilityModel={organizationsColumnVisibility}
						visibilityModel={columnVisibilityModel}
						setVisibilityModel={setColumnVisibilityModel}
						tableApiRef={tableAPIRef}
					/>
				</div>
				<OrangeButton
					title={"Add New Account"}
					className={"px-5 h-10 flex items-center "}
					onClick={() => {
						setOpenAccountModalForm(true);
						setIsEditForm(false);
					}}
				/>
			</div>
			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.ACCOUNT_MANAGEMENT}
				data={tableData}
				columns={accountManagementColumns}
				columnVisibility={columnVisibilityModel}
				onColumnVisibilityChange={setColumnVisibilityModel}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={organizationsColumnVisibility}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			<Popover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				PaperProps={{
					style: {
						width: "auto",
						borderRadius: 8,
						display: "flex",
						justifyContent: "center",
					},
				}}
			>
				<div className="flex flex-col justify-center p-3 text-secondary-darkgray">
					<div
						onClick={() => {
							setOpenAccountModalForm(true);
							setIsEditForm(true);
							handleClose();
						}}
						className="table-action-popover"
					>
						<BsPencil />
						<p className="text-2sm">Edit</p>
					</div>
					<div
						onClick={() => {
							setOpenViewDetailsModal(true);
							handleClose();
						}}
						className="table-action-popover"
					>
						<LuEye />
						<p className="text-2sm">View Details</p>
					</div>
					{(formDataValues?.status === "ACTIVE" ||
						formDataValues?.status === "SUSPENDED_PENDING_PAYMENT") && (
						<div
							onClick={() => {
								setOpenAccountActionModal(true);
								setAccountActionMode("SUSPEND");
								handleClose();
							}}
							className="table-action-popover"
						>
							<HiOutlineLockClosed />
							<p className="text-2sm">Suspend</p>
						</div>
					)}
					{(formDataValues?.status === "SUSPENDED" ||
						formDataValues?.status === "SUSPENDED_PENDING_PAYMENT") && (
						<div
							onClick={() => {
								setOpenAccountActionModal(true);
								setAccountActionMode("ACTIVATE");
								handleClose();
							}}
							className="table-action-popover"
						>
							<HiOutlineLockOpen />
							<p className="text-2sm">Activate</p>
						</div>
					)}
				</div>
			</Popover>
			{openViewDetailsModal && (
				<ViewOrganizationInfo
					organization={formDataValues}
					open={openViewDetailsModal}
					setOpen={setOpenViewDetailsModal}
				/>
			)}
			{openAccountModalForm && (
				<AccountModalForm
					open={openAccountModalForm}
					setOpen={setOpenAccountModalForm}
					title={
						isEditForm ? "Update " + formDataValues?.name : "Create New Account"
					}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					countries={countries?.data || []}
					billingTypes={billingTypes}
					reloadTableData={reloadTableData}
				/>
			)}
			{openAccountActionModal && (
				<AccountActionModal
					mode={accountActionMode}
					open={openAccountActionModal}
					setOpen={setOpenAccountActionModal}
					tableAPIRef={tableAPIRef}
				/>
			)}
		</AdminLayout>
	);
};

export default AccountsManagement;
